import * as React from "react";
import {IFeesTreeNode} from "../../model/Portfolio.model";
import {Body, Cell, Header, HeaderCell, HeaderRow, Row, Table} from "@table-library/react-table-library/table";
import {buildFeeTree, PLAN_TREE_THEME} from "./portfolioUtil";
import {CellTree, Tree, useTree} from "@table-library/react-table-library/tree";
import {Link} from "react-router-dom";
import {useEffect, useState} from "react";
import {IPortfolioFeesTreeNode, PEER_UNIVERSE_MEDIAN} from "../../api/PortfolioApi";
import {AonColors} from "../../../../css/Colors";
import {FeesPopup} from "./FeesPopup";

export interface IFeesTabProps {
    portfolioFees: IPortfolioFeesTreeNode[]
}

export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_1 = "The investment fee analysis reported in the table above measures only the relative fee level of your current plan offerings as compared to fee medians from style, size, and vehicle-specific peer universes, which Aon Investments believes is an appropriate comparison. Determining whether an investment's expense is reasonable depends on a variety of factors including the level of returns achieved and the method with which administration and operational expenses are paid. Understanding and evaluating the total cost of plan operations, including both investment and administration fees is a critical analysis that should be completed periodically.";
export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_2 = "Expense Ratio is intended to represent a fund's total expense ratio which may include investment management fees, 12b-1 fees, custodial fees, trustee fees, revenue sharing components and/or charges for fund or plan administration. Expense ratios shown reflect the current fee and may not match your quarterly investment report.";
export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_3 = "Fee universes are provided and calculated by eVestment Alliance, as of prior year end. Fund expense information has been sourced from eVestment for all asset classes except the stable value and target date fund universes, which uses Morningstar databases. Since fund expense ratios do not change frequently, Aon Investments believes this study to be a relevant comparison for up to a year, at which time it will be updated.";
export const PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_4 = "For complete information on methodologies, contact your Aon Investments consultant.";

export const FeesTab: React.FC<IFeesTabProps> = (props) => {
    const [nodes, setNodes] = useState<IFeesTreeNode[]>([]);
    const [displayPopup, setDisplayPopup] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<number>(-1);
    const [importantNoteOpen, setImportantNoteOpen] = useState<boolean>(false);
    const [popupYAxisPosition, setPopupYAxisPosition] = useState<number>(0);

    useEffect(() => {
        setNodes( buildFeeTree(props.portfolioFees));
    }, [props]);

    const closePopup = () => {
        setDisplayPopup(false);
        setSelectedId(-1);
    };

    const medianUniverseClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const medianPopupHeight = 271;
        const offsetY = 20;
        const visibleHeight = window.innerHeight;

        let calculatedYAxis = e.clientY + offsetY;

        if((calculatedYAxis + medianPopupHeight) > visibleHeight) {
            calculatedYAxis = e.clientY - medianPopupHeight - offsetY;
        }
        setPopupYAxisPosition(calculatedYAxis);
    };

    const data = {nodes};

    const renderName = (item: IFeesTreeNode) => {
        return item.backstopId
            ? <Link to={`/products/${item.backstopId}`} id={`link-${item.id}`}>
                {item.name}
            </Link>
            : item.name;
    };

    const expandCollapse = () => {
        tree.fns.onToggleAll({isCarryForward: false, isPartialToAll: true});
    };

    const tree: Tree<IFeesTreeNode> = useTree(data,
        undefined,
        {treeIcon: {
                iconRight: (
                    <div className="far fa-chevron-right fa-chevron-tree"/>
                ),
                iconDown: (
                    <div className="far fa-chevron-down  fa-chevron-tree"/>
                ),
            }},
    );

    const getCellStyle = (value: string | null) => {
        const styles: Record<string, {color: string}> = {
            [PEER_UNIVERSE_MEDIAN.ABOVE_MEDIAN]: {color: AonColors.AonErrorRed},
            [PEER_UNIVERSE_MEDIAN.BELOW_MEDIAN]: {color: AonColors.AonSuccessGreen},
            [PEER_UNIVERSE_MEDIAN.AT_MEDIAN]: {color: AonColors.AonWarningDark},
            [PEER_UNIVERSE_MEDIAN.UNKNOWN]: {color: AonColors.AonGray01}
        };
        return value === null ? {} : styles[value] ?? {};
    };

    const renderPopup = () => {
        const selectedNode = props.portfolioFees.find(node => node.id === selectedId)!;

        return <FeesPopup
            selectedNode={selectedNode}
            yAxisPosition={popupYAxisPosition}
            closePopup={closePopup}/>;
    };

    const renderTree = () => {
        return (
            <Table data={data}
                   tree={tree}
                   theme={PLAN_TREE_THEME("32% 30% 9% 8% 11% 10%")}
                   layout={{custom: true, fixedHeader: true}}>
                {(nodes: IFeesTreeNode[]) => (
                    <>
                        <Header>
                            <HeaderRow>
                                <HeaderCell>Category</HeaderCell>
                                <HeaderCell className={"fees-tab__peer-universe-name"}>Peer Universe</HeaderCell>
                                <HeaderCell>Net Expense<br/> Ratio (%)</HeaderCell>
                                <HeaderCell>Revenue<br/> Share (%)</HeaderCell>
                                <HeaderCell>Effective Net<br/> Expense Ratio (%)</HeaderCell>
                                <HeaderCell>Above/Below Peer<br/> Universe Median</HeaderCell>
                            </HeaderRow>
                        </Header>

                        <Body>
                            {nodes.map((item: IFeesTreeNode) => (
                                <Row key={item.id} item={item}
                                     className={item.nodes && item.nodes.length > 0 ? "portfolio__parent-node" : ""}>
                                    <CellTree item={item}>
                                        {renderName(item)}
                                    </CellTree>
                                    <Cell className={"fees-tab__peer-universe-name"}>{item.peerUniverseName}</Cell>
                                    <Cell>{item.netExpenseRatio?.toFixed(3)}</Cell>
                                    <Cell>{item.revenueShare?.toFixed(3)}</Cell>
                                    <Cell>
                                        {item.effectiveNetExpenseRatio?.toFixed(3)}
                                    </Cell>
                                    <Cell className={"fees-tab__peer-universe-median"}>
                                        {item.peerUniverseMedian !== PEER_UNIVERSE_MEDIAN.UNKNOWN
                                            ? <div onClick={medianUniverseClick}>
                                                <div  className={"clickable"} style={getCellStyle(item.peerUniverseMedian)}
                                                 onClick={() => {
                                                     setDisplayPopup(!displayPopup);
                                                     setSelectedId(item.id);
                                                }
                                                 }>{item.peerUniverseMedian}</div></div>
                                            : <div style={getCellStyle(item.peerUniverseMedian)}>
                                                {item.peerUniverseMedian}
                                            </div>
                                        }
                                    </Cell>
                                </Row>
                            ))}
                        </Body>
                    </>
                )}
            </Table>
        );
    };

    const renderImportantNoteText = () => {
        return <div className={"fees-tab__important-note-text"} data-testid="fees-tab__important-note-text">
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_1}</p>
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_2}</p>
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_3}</p>
            <p>{PORTFOLIO_FEES_IMPORTANT_NOTE_PARA_4}</p>
        </div>;
    };

    const renderImportantNote = () => {
        return <div className={"fees-tab__important-note-container"}>
          <span className="fees-tab__important-note-toggle clickable link-16"
                data-testid="fees-tab__important-note-toggle"
                onClick={() => setImportantNoteOpen(!importantNoteOpen)}>
                    Important Note
                    <span className={importantNoteOpen ? "fal fa-angle-up" : "fal fa-angle-down"}
                          data-testid={"fees-tab__important-note-icon"}/>
            </span>
            {importantNoteOpen && renderImportantNoteText()}
        </div>;
    };

    return <div className="portfolio__tree-container" data-testid="fees-tab__container">
        <div className="portfolio__header-container">
            <div className="portfolio__expand-collapse-container">
                <div className="portfolio__expand-collapse-text link-14 clickable fees" onClick={expandCollapse}>
                    Expand/Collapse All
                </div>
            </div>
        </div>
        <div className="portfolio__tree-table-container">
            {renderTree()}
            { displayPopup && renderPopup()}
        </div>
        {renderImportantNote()}
    </div>;
};